import { Button, ConfigProvider, Dropdown, Modal, notification, Space, Table, Typography } from 'antd';
import { useDeleteMaterialRequestMutation, useGetMaterialRequestQuery } from '../../../redux/api/material-request';
import { Lineitem, ProductionMethod } from '../../../interfaces/Ordel.Interface';
import { showMaterialRequestModal } from '../../../redux/slices/material-request';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import MaterialRequestModal from '../../../components/MaterialRequestModal';
import RequestAdditionalMaterialModal from '../../../components/RequestAdditionalMaterialModal';
import { DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { THEME } from '../../../main.tsx';
import { useState } from 'react';
import { useAddMaterialOrderDraftMutation } from '../../../redux/api/material.ts';
import { useNavigate } from 'react-router';
import { useHasMultipleBuildings } from '../../../hooks/useHasMultipleBuildings';
import { getDisplaySize } from '../../../utils';

const { Title } = Typography;

interface Props {
	lineItem: Lineitem;
	refetchLineItem: () => void;
    actionsDisabled?: boolean;
}

const MaterialRequests = (props: Props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const hasMultipleBuildings = useHasMultipleBuildings();

	const [creatingMaterialOrder, setCreatingMaterialOrder] = useState(false);

	const materialRequestsQuery = useGetMaterialRequestQuery({
		order_line_item: props.lineItem.id,
		ordering: '-created_time',
	});

	const [deleteMaterialRequest] = useDeleteMaterialRequestMutation();
	const [selectedMaterialRequest, setSelectedMaterialRequest] = useState<any>();
	const [isRequestMoreMaterialVisible, setIsRequestMoreMaterialVisible] = useState(false);
	const [addMaterialOrderDraft] = useAddMaterialOrderDraftMutation();

	return (
		<>
			<div>
				<div>
					<Title level={5}>Material Requests</Title>
				</div>
				<Button
					onClick={() => {
						dispatch(showMaterialRequestModal());
					}}
					type="primary"
					className="mb-3"
					disabled={props.actionsDisabled || props.lineItem.status === 'Cancelled'}
				>
					Request Material
				</Button>
				<Table
					loading={materialRequestsQuery.isLoading}
				columns={[
					{
						title: 'Material',
						render: (record) =>
							`${record?.vendor_material?.type?.display_name} ${record?.vendor_material?.shape?.display_name}`,
					},
					...(hasMultipleBuildings ? [{
						title: 'Building',
						key: 'building',
						render: (record: any) => record?.building?.name || '—',
					}] : []),
					{
						title: 'Size',
						render: (record) => {
							const vm = record.vendor_material;
							return vm
								? (vm.display_size || getDisplaySize({
										material_shape: vm.shape?.display_name,
										material_height: vm.height,
										material_width: vm.width,
										material_outer_diameter: vm.outer_diameter,
										material_inner_diameter: vm.inner_diameter,
										material_wall_thickness: vm.wall_thickness,
									}))
								: record.display_size || '';
						},
					},
					{
						title: props.lineItem?.partsbadger_production_method === ProductionMethod.Swiss ? 'Part Length' : 'Cut Length',
						render: (record) => `${record.material_cut_length} in.`,
					},
					{
						title: 'Total Required Length',
						render: (record) => `${record.total_required_length} in.`,
					},
				{
					title: 'Status',
					render: (record) => record.status,
				},
					{
						title: 'Sumbitted',
							render: (record) =>
								dayjs(record.created_time).format('MMM DD, YYYY'),
						},
					{
						title: 'Created By',
						render: (record) => record.created_by || '—',
					},
						{
							render: (record) => (
								<Space>
									<Button.Group>
										<Button
											disabled={['Ordered', 'Reserved'].includes(record.status)}
											loading={creatingMaterialOrder}
											onClick={async () => {
												if (typeof record.order_line_item === 'object') {
													dispatch(showMaterialRequestModal());
													setSelectedMaterialRequest(record);
												}
											}}
										>
											Edit
										</Button>

										<Dropdown
											menu={{
												items: [
													{
														label: 'Request More',
														key: '1',
														icon: <PlusOutlined />,
														disabled: props.actionsDisabled,
														onClick: () => {
															setSelectedMaterialRequest(record);
															setIsRequestMoreMaterialVisible(true);
														},
													},
													{
														label: 'Create Material Order',
														key: '3',
														icon: <PlusOutlined />,
														disabled: props.actionsDisabled,
														onClick: () => {
															addMaterialOrderDraft({
																material_order_items: [
																	{
																		material_order_request: record.id,
																	},
																],
															})
																.unwrap()
																.then((res) => {
																	navigate(`/material/orders/add/${res.id}`);
																})
																.catch(() => {
																	notification.error({
																		duration: 10,
																		message: 'Error',
																		description: 'The material request cannot be converted.',
																		placement: 'topRight',
																	});
																});
														},
													},
													{
														label: 'Delete',
														key: '2',
														icon: <DeleteOutlined />,
														disabled: ['Ordered', 'Reserved'].includes(record.status),
														onClick: () => {
															Modal.confirm({
																title: 'Delete Material Request?',
																centered: true,
																icon: null,
																onOk: async () => {
																	await deleteMaterialRequest({
																		id: record.id,
																	})
																		.unwrap()
																		.then(() => {
																			materialRequestsQuery.refetch();
																			props.refetchLineItem();
																		});
																},
																footer: (_, { OkBtn, CancelBtn }) => (
																	<ConfigProvider theme={THEME}>
																		<CancelBtn />
																		<OkBtn />
																	</ConfigProvider>
																),
															});
														},
													},
												],
											}}
										>
											<Button icon={<DownOutlined />} />
										</Dropdown>
									</Button.Group>
								</Space>
								// <MaterialRequestActions
								// 	materialRequest={record}
								// 	lineItem={props.lineItem}
								// 	refetch={() => {
								// 		materialRequestsQuery.refetch();
								// 		props.refetchLineItem();
								// 	}}
								// />
							),
						},
					]}
					dataSource={materialRequestsQuery?.data?.results}
					size="small"
				/>
			</div>

			<RequestAdditionalMaterialModal
				visible={isRequestMoreMaterialVisible}
				setVisible={setIsRequestMoreMaterialVisible}
				material_request={selectedMaterialRequest}
				refetchData={() => {
					materialRequestsQuery.refetch();
					props.refetchLineItem
				}}
			/>

			<MaterialRequestModal
				lineItem={props.lineItem}
				refetchData={() => {
					materialRequestsQuery.refetch();
					props.refetchLineItem();
				}}
				refetchLineItem={props.refetchLineItem}
				materialRequest={selectedMaterialRequest}
			/>
		</>
	);
};

export default MaterialRequests;
